import React, { useEffect } from "react"
import * as Yup from "yup"
import { useFormik } from "formik"
import { useAtom as GetAtom } from "jotai"
import { ShopPaymentPoint } from "../../libs/api/backend/src"
import { formatPriceKZT, formatPercent } from "@malond/utils"
import { cn } from "@malond/ui/utils"
import Label from "../Elements/Form/Label"
import Input from "../Elements/Form/Input"
import FormError from "../Elements/Form/FormError"
import {
  checkoutDiscountAtom,
  checkoutRaisePercentAtom,
  checkoutSumBackAtom,
  checkoutSumGiveAtom,
  checkoutSumRaiseAtom,
  checkoutSumsAtom,
  checkoutSumTotalAtom,
} from "../../store/checkout"
import { ItemNumberValidation } from "../../libs/validation/validation"

interface CheckoutMainBySelectedPaymentMethodProps {
  selectedPaymentMethod?: ShopPaymentPoint
}

interface ValuesProps {
  sumGive: string
}

const validationSchema = Yup.object({
  sumGive: ItemNumberValidation,
})

export default function CheckoutMainBySelectedPaymentMethod({
  selectedPaymentMethod,
}: CheckoutMainBySelectedPaymentMethodProps) {
  // Atoms
  const [checkoutSumTotal] = GetAtom(checkoutSumTotalAtom)
  const [checkoutDiscount] = GetAtom<string>(checkoutDiscountAtom)
  const [raisePercent] = GetAtom(checkoutRaisePercentAtom)
  const [raisePercentSum] = GetAtom(checkoutSumRaiseAtom)
  const [checkoutSums] = GetAtom(checkoutSumsAtom)

  const [checkoutSumGive, setCheckoutSumGive] = GetAtom(checkoutSumGiveAtom)
  const [checkoutSumBack] = GetAtom(checkoutSumBackAtom)

  const priceDiscountNum = Number(checkoutSums.priceDiscount) || 0
  const discountNum = Number(checkoutDiscount) || 0
  const raisePercentNum = Number(raisePercent) || 0
  const raisePercentSumNum = Number(raisePercentSum) || 0
  const sumBackNum = Number(checkoutSumBack) || 0

  const formik = useFormik({
    initialValues: {
      sumGive: "0",
    },
    validationSchema: validationSchema,
    onSubmit: () => {},
  })

  useEffect(() => {
    if (selectedPaymentMethod?.typeOfPayment === "CASH") {
      setCheckoutSumGive("0")
    } else {
      setCheckoutSumGive(checkoutSumTotal)
    }
  }, [selectedPaymentMethod?.typeOfPayment, checkoutSumTotal])

  const onChangeValue = async (fieldName: string, value: any) => {
    const num = Number(value) || 0

    if (
      fieldName === "sumGive" &&
      selectedPaymentMethod?.typeOfPayment === "CASH"
    ) {
      await formik.setFieldValue(fieldName, String(num), true)
      await setCheckoutSumGive(String(num))
    }
  }

  if (!selectedPaymentMethod) {
    return (
      <p className="text-center text-xl font-bold text-destructive">
        Выбранный метод оплаты неизвестен
      </p>
    )
  }

  return (
    <div>
      <h1 className="mb-8 text-2xl font-bold text-foreground">
        {selectedPaymentMethod.title}
      </h1>

      <div className="grid grid-cols-2 gap-3 text-base">
        <div>
          <h3 className="text-muted-foreground">Сумма</h3>
        </div>
        <div className="text-right">
          <h3 className="font-semibold text-foreground">
            {formatPriceKZT(checkoutSums.priceDiscount)}
          </h3>
        </div>

        <div>
          <h3 className="text-muted-foreground">Надбавка</h3>
        </div>
        <div className="text-right">
          <h3 className="font-semibold text-foreground">
            {raisePercentNum > 0 ? (
              <>
                {formatPercent(raisePercent)} (
                {formatPriceKZT(raisePercentSumNum)})
              </>
            ) : (
              "0 ₸"
            )}
          </h3>
        </div>

        <div>
          <h3 className="text-muted-foreground">Скидка на чек</h3>
        </div>
        <div className="text-right">
          <h3 className="font-semibold text-foreground">
            {discountNum > 0 ? (
              <>
                ~
                {formatPercent(
                  priceDiscountNum > 0
                    ? Math.round((discountNum / priceDiscountNum) * 100)
                    : 0
                )}{" "}
                ({formatPriceKZT(discountNum)})
              </>
            ) : (
              "0 ₸"
            )}
          </h3>
        </div>

        <div className="border-t border-border pt-2">
          <h3 className="text-lg font-bold text-foreground">Итого к оплате</h3>
        </div>
        <div className="border-t border-border pt-2 text-right">
          <h3 className="text-xl font-bold text-primary">
            {formatPriceKZT(checkoutSumTotal)}
          </h3>
        </div>

        <div>
          <h3 className="text-muted-foreground">Принято от клиента</h3>
        </div>
        <div className="text-right">
          <h3 className="font-semibold text-foreground">
            {formatPriceKZT(checkoutSumGive)}
          </h3>
        </div>

        <div>
          <h3 className="text-muted-foreground">Сдача</h3>
        </div>
        <div className="text-right">
          <h3
            className={cn(
              "text-lg font-bold",
              sumBackNum > 0
                ? "text-emerald-600 dark:text-emerald-400"
                : "text-foreground"
            )}
          >
            {sumBackNum >= 0 ? formatPriceKZT(sumBackNum) : "0 ₸"}
          </h3>
        </div>
      </div>

      {/* CASH */}
      <div
        className={cn("mt-8", {
          hidden: selectedPaymentMethod.typeOfPayment !== "CASH",
        })}
      >
        <Label
          htmlFor={"sumGive"}
          isError={Boolean(formik.errors.sumGive && formik.touched.sumGive)}
        >
          Принято от клиента (₸)
        </Label>
        <Input
          type="number"
          name="sumGive"
          value={formik.values.sumGive}
          onChange={(e) => onChangeValue("sumGive", e.target.value)}
          onBlur={(e) => onChangeValue("sumGive", e.target.value)}
          withLabel={true}
          isError={Boolean(formik.errors.sumGive && formik.touched.sumGive)}
        />

        <FormError
          isError={Boolean(formik.errors.sumGive && formik.touched.sumGive)}
        >
          {formik.errors.sumGive}
        </FormError>
      </div>

      {/* CARD & QR */}
      <p
        className={cn(
          "mt-8 rounded-lg border border-border bg-muted/50 p-4 text-sm text-muted-foreground",
          {
            hidden:
              selectedPaymentMethod.typeOfPayment !== "CARD" &&
              selectedPaymentMethod.typeOfPayment !== "QR",
          }
        )}
      >
        Произведите оплату на терминале, дождитесь ответа и нажмите кнопку
        «Далее» для завершения продажи
      </p>
    </div>
  )
}
